import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFactoryDetail, listFactories } from '../services/factories';
import { isAdmin } from '../services/api';
import { HIERARCHY_LABELS } from '../utils/hierarchyLabels';
import {
  LOCATION_LEVELS,
  buildCrumbs,
  getLevelItems,
  isCameraVisibleForPage,
  isSamePosition,
  normalizePosition,
  openItemPosition,
  positionAtLevel,
  pruneHierarchy,
  resolveBackTarget,
} from '../utils/shortestHierarchy';

/**
 * Доступное дерево для кратчайшей иерархии (ТЗ №24).
 * GET /factories + GET /factories/{id} (backend отдаёт иерархию, доступную пользователю),
 * затем скрываются ветви без камер типа страницы.
 * Деталь одного объекта не загрузилась — пропускается только этот объект.
 * status 'error' (полная иерархия, как раньше) — если не загрузился список объектов
 * или не загрузилась ни одна деталь.
 * @returns {{ status: 'loading'|'ready'|'error', tree: Array|null }}
 */
export const useHierarchyTree = (cameraType) => {
  const [state, setState] = useState({ status: 'loading', tree: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', tree: null });

    (async () => {
      try {
        const factories = await listFactories();
        if (!Array.isArray(factories)) throw new Error('factories list is not an array');
        const results = await Promise.allSettled(factories.map((factory) => getFactoryDetail(factory.id)));
        const details = [];
        results.forEach((result, index) => {
          const ok = result.status === 'fulfilled' && result.value && typeof result.value === 'object';
          if (ok) details.push(result.value);
          else console.error(`Не удалось загрузить объект ${factories[index]?.id}`, result.reason);
        });
        if (factories.length > 0 && details.length === 0) throw new Error('no factory detail loaded');
        const admin = isAdmin();
        const tree = pruneHierarchy(details, (camera) => isCameraVisibleForPage(camera, cameraType, admin));
        if (!cancelled) setState({ status: 'ready', tree });
      } catch (e) {
        console.error('Ошибка загрузки иерархии', e);
        if (!cancelled) setState({ status: 'error', tree: null });
      }
    })();

    return () => { cancelled = true; };
  }, [cameraType]);

  return state;
};

/**
 * Навигация страниц кадров (FramesPage / EquipmentPage) по кратчайшей иерархии.
 * Принимает состояние useNavigationState и сам приводит восстановленную позицию к кратчайшей.
 *
 * locationData — элементы текущего уровня-локации из дерева (null — грузить как раньше);
 * locationPending — дерево ещё грузится или позиция ещё не приведена, данные не показывать.
 */
export const useShortestHierarchy = (cameraType, nav) => {
  const { currentLevel, selectedItems, setCurrentLevel, setSelectedItems } = nav;
  const { status, tree: loadedTree } = useHierarchyTree(cameraType);
  const tree = status === 'ready' ? loadedTree : null;
  const loading = status === 'loading';

  const applyPosition = useCallback((position) => {
    if (!position) return;
    setSelectedItems(position.items);
    setCurrentLevel(position.level);
  }, [setCurrentLevel, setSelectedItems]);

  const normalized = useMemo(
    () => (tree ? normalizePosition(tree, currentLevel, selectedItems) : null),
    [tree, currentLevel, selectedItems]
  );
  const isNormalized = !normalized || isSamePosition(normalized, { level: currentLevel, items: selectedItems });

  // Восстановленная позиция (в т. ч. из старой полной навигации) — к кратчайшей.
  useEffect(() => {
    if (!isNormalized) applyPosition(normalized);
  }, [isNormalized, normalized, applyPosition]);

  const isLocationLevel = LOCATION_LEVELS.includes(currentLevel);
  const locationPending = isLocationLevel && (loading || !isNormalized);
  const locationData = useMemo(
    () => (tree && isLocationLevel ? getLevelItems(tree, currentLevel, selectedItems) || [] : null),
    [tree, isLocationLevel, currentLevel, selectedItems]
  );

  // Пока дерево грузится, «Назад» неактивна: цель может оказаться пропускаемым уровнем.
  const backTarget = loading ? null : resolveBackTarget(tree, currentLevel, selectedItems);
  const crumbs = buildCrumbs(tree, selectedItems, HIERARCHY_LABELS.factory.plural);

  const openItem = useCallback(
    (item, itemLevel) => applyPosition(openItemPosition(tree, itemLevel, item, selectedItems)),
    [tree, selectedItems, applyPosition]
  );
  const goToLevel = useCallback(
    (level) => applyPosition(positionAtLevel(level, selectedItems)),
    [selectedItems, applyPosition]
  );
  const goBack = useCallback(() => applyPosition(backTarget), [backTarget, applyPosition]);

  return {
    tree,
    loading,
    locationData: locationPending ? null : locationData,
    locationPending,
    backTarget,
    crumbs,
    openItem,
    goToLevel,
    goBack,
  };
};

export default useShortestHierarchy;
